import React, {useCallback, useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import PropTypes from "prop-types";
import {Box, Button, CircularProgress, Typography} from "@mui/material";
import {useMutation, useQuery} from "@apollo/client";
import {
  GET_NOTIFICATION_CENTER,
  MARK_NOTIFICATION_AS_READ,
  NOTIFICATION_PAGE_SIZE,
} from "../../graphql/notificationCenterQueries";
import {usePermissions} from "../../context/AuthzProvider";
import NotificationCard from "./NotificationCard";
import {mergeById} from "../../resources/utils/arrayUtils";
import {useNotificationListStyles} from "./notificationCenterStyles";
import {map} from "underscore";

const NotificationList = ({category, onClose}) => {
  const {classes} = useNotificationListStyles();
  const navigate = useNavigate();
  const {authorizedToRoute} = usePermissions();
  const [hasMore, setHasMore] = useState(true);
  useEffect(() => setHasMore(true), [category]);
  const {data, loading, error, fetchMore} = useQuery(GET_NOTIFICATION_CENTER, {
    variables: {category, limit: NOTIFICATION_PAGE_SIZE},
    fetchPolicy: "network-only",
    nextFetchPolicy: "cache-first",
  });
  const [markAsRead] = useMutation(MARK_NOTIFICATION_AS_READ);

  const notifications = data?.notificationCenter || [];

  const handleClick = useCallback(
    (notification) => {
      if (!notification.isRead) {
        markAsRead({variables: {id: notification.id}});
      }
      if (notification.url && authorizedToRoute(notification.url)) {
        if (notification.model === "meeting") {
          window.open(notification.url, "videoConference", "width=1200,height=800");
        } else {
          navigate(notification.url);
        }
      }
      onClose();
    },
    [markAsRead, navigate, onClose, authorizedToRoute],
  );

  const handleLoadMore = () => {
    const lastId = notifications[notifications.length - 1]?.id;
    fetchMore({
      variables: {cursor: lastId},
      updateQuery: (prev, {fetchMoreResult}) => {
        if (!fetchMoreResult) {
          return prev;
        }
        const newItems = fetchMoreResult.notificationCenter;
        if (newItems.length < NOTIFICATION_PAGE_SIZE) {
          setHasMore(false);
        }
        return {
          notificationCenter: mergeById(prev.notificationCenter, newItems),
        };
      },
    });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error) {
    return (
      <Typography color="error" className={classes.empty}>
        Error loading notifications.
      </Typography>
    );
  }

  if (notifications.length === 0) {
    return <Typography className={classes.empty}>No notifications yet.</Typography>;
  }

  return (
    <Box className={classes.list}>
      {map(notifications, (n) => (
        <NotificationCard key={n.id} notification={n} onClick={handleClick} />
      ))}
      {hasMore && notifications.length >= NOTIFICATION_PAGE_SIZE && (
        <Box className={classes.loadMore}>
          <Button onClick={handleLoadMore} size="small">
            Load More
          </Button>
        </Box>
      )}
    </Box>
  );
};

NotificationList.propTypes = {
  category: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default NotificationList;
